import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Space, Typography } from 'antd';
import 'chart.js/auto';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { MindmatchAttributes } from '../../../types';
import PolarAreaChartWithLabel from './PolarAreaChartWithLabel';
import { findTwoLargestValuesIndex } from './Utils/findTwoLargestValuesIndex';

interface AttributeProps {
  attributesData?: MindmatchAttributes[];
  hasAttributes: boolean;
}

const TestProfileMap: React.FC<AttributeProps> = ({ attributesData, hasAttributes = false }) => {
  const order = [
    // Atributos mentais
    'Capacidade analítica',
    'Pensamento conceitual',
    'Reflexão',
    'Pensamento criativo',
    'Planejamento e organização',
    // Atributos sociais
    'Comunicação',
    'Consideração pelos outros',
    'Influência',
    'Sociabilidade',
    'Facilitação',
    'Flexibilidade',
    // Atributos motivacionais
    'Estabilidade emocional',
    'Ambição',
    'Iniciativa',
    'Assertividade',
    'Tomada de riscos',
  ];

  const labels: string[] = [];
  const data: number[] = [];

  if (attributesData) {
    order.forEach((value: string) => {
      const attributeObject = attributesData.find(
        (attribute_dict) => attribute_dict['name'] === value
      );
      if (attributeObject) {
        labels.push(value);
        data.push(attributeObject['value']);
      }
    });
  }

  let backgroundColor;
  if (hasAttributes) {
    backgroundColor = [
      // Azul - Mental
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      // Verde - Social
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      // Vermelho - Motivacional
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
    ];
  } else {
    const darkBlue = '#002529';
    backgroundColor = [
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
    ];

    const twoLargestValuesIndex = findTwoLargestValuesIndex(data);
    backgroundColor[twoLargestValuesIndex[0]] = darkBlue;
    backgroundColor[twoLargestValuesIndex[1]] = darkBlue;
  }

  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

  return (
    <Col>
      {isInfoModalOpen && (
        <Modal
          title={<Typography.Title level={3}>Atributos</Typography.Title>}
          open={isInfoModalOpen}
          width={900}
          cancelButtonProps={{
            hidden: true,
          }}
          onOk={() => setIsInfoModalOpen(false)}
        >
          <Helmet>
            <title>Atributos | Recrutamento e Seleção</title>
          </Helmet>
          <Typography.Paragraph>
            <p>
              <strong>Capacidade analítica:</strong> Capacidade para analisar informações, números,
              dados, etc; processar estas informações de maneira lógica; chegar a conclusões
              fundamentadas.
            </p>
            <p>
              <strong>Pensamento conceitual:</strong> Capacidade de formular conceitos, determinar
              problemas e lidar com abstrações.
            </p>
            <p>
              <strong>Reflexão:</strong> Capacidade de reflexão, autoanálise e observação dos seus
              estados e pontos fortes e fracos.
            </p>
            <p>
              <strong>Pensamento criativo:</strong> Capacidade de criar coisas novas e inovar; fugir
              do lugar comum.
            </p>
            <p>
              <strong>Planejamento e organização:</strong> Capacidade de manter as coisas em ordem;
              controlar e colocar estrutura.
            </p>
            <p>
              <strong>Comunicação:</strong> Capacidade de se comunicar verbalmente de forma clara;
              transmitir ideias através de palavras para indivíduos ou audiências.
            </p>
            <p>
              <strong>Consideração pelos outros:</strong> Capacidade de empatizar e de levar a
              opinião e o sentimento das outras pessoas em consideração nas interações e nas
              decisões.
            </p>
            <p>
              <strong>Influência:</strong> Capacidade de persuadir outras pessoas a se comportar de
              maneira diferente do que teriam sem intervenção.
            </p>
            <p>
              <strong>Sociabilidade:</strong> Capacidade de interagir e criar relacionamento com
              pessoas diferentes; facilidade em ambientes sociais.
            </p>
            <p>
              <strong>Facilitação:</strong> Capacidade de entender situações sociais, colaborar e
              solucionar conflitos de maneira harmoniosa.
            </p>
            <p>
              <strong>Flexibilidade:</strong> Capacidade de adaptar-se a diferentes ambientes de
              maneira sutil.
            </p>
            <p>
              <strong>Estabilidade emocional:</strong> Capacidade de manter a calma em situações de
              pressão e de controlar altos e baixos emocionais.
            </p>
            <p>
              <strong>Ambição:</strong> Capacidade de auto motivar-se na busca de altos padrões e de
              crescimento.
            </p>
            <p>
              <strong>Iniciativa:</strong> Capacidade de iniciar ações sem a necessidade de
              intervenção de terceiros; tomar a frente.
            </p>
            <p>
              <strong>Assertividade:</strong> Capacidade de colocar as suas ideias de forma direta;
              não se deixar intimidar pelo ambiente.
            </p>
            <p>
              <strong>Tomada de riscos:</strong> Capacidade de agir mesmo com altos graus de
              incerteza, sem se abalar.
            </p>
          </Typography.Paragraph>
        </Modal>
      )}
      <Row>
        <Space size="small" align="center">
          <Typography.Title level={5}>Mapeamento de Perfil</Typography.Title>
          <Button
            type="link"
            htmlType="submit"
            icon={<QuestionCircleOutlined />}
            style={{ paddingBottom: '10px' }}
            onClick={() => setIsInfoModalOpen(true)}
          />
        </Space>
      </Row>
      <PolarAreaChartWithLabel
        labels={labels}
        data={data}
        backgroundColor={backgroundColor}
        labelFontSize={10}
      />
    </Col>
  );
};

export default TestProfileMap;
